// Function to determine if a color is dark (accounts for opacity by compositing over white)
export const isColorDark = (color: string | undefined): boolean => {
  if (!color) return false;

  type RGBA = { r: number; g: number; b: number; a: number };

  const clamp0To255 = (value: number): number => {
    return Math.max(0, Math.min(255, Math.round(value)));
  };

  const parseHex = (hexInput: string): RGBA | null => {
    const hex = hexInput.trim().replace("#", "");
    if (hex.length === 3) {
      // #RGB
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return { r, g, b, a: 1 };
    }
    if (hex.length === 4) {
      // #RGBA
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      const a = parseInt(hex[3] + hex[3], 16) / 255;
      return { r, g, b, a };
    }
    if (hex.length === 6) {
      // #RRGGBB
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      return { r, g, b, a: 1 };
    }
    if (hex.length === 8) {
      // #RRGGBBAA
      const r = parseInt(hex.substring(0, 2), 16);
      const g = parseInt(hex.substring(2, 4), 16);
      const b = parseInt(hex.substring(4, 6), 16);
      const a = parseInt(hex.substring(6, 8), 16) / 255;
      return { r, g, b, a };
    }
    return null;
  };

  const parseRgbFunction = (input: string): RGBA | null => {
    const lower = input.trim().toLowerCase();
    if (!lower.startsWith("rgb")) return null;
    const match = lower.match(/rgba?\s*\(\s*([^)]+)\)/i);
    if (!match) return null;

    // Support both comma and space separated values, and optional "/ alpha"
    const parts = match[1]
      .split(/[\s,/]+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);

    if (parts.length < 3) return null;

    const parseRgbComponent = (value: string): number => {
      // Support percentages (e.g., 50%)
      if (value.endsWith("%")) {
        const pct = Number.parseFloat(value.slice(0, -1));
        if (Number.isNaN(pct)) return 0;
        return clamp0To255((pct / 100) * 255);
      }
      const n = Number.parseFloat(value);
      if (Number.isNaN(n)) return 0;
      return clamp0To255(n);
    };

    const parseAlpha = (value: string | undefined): number => {
      if (value === undefined) return 1;
      if (value.endsWith("%")) {
        const pct = Number.parseFloat(value.slice(0, -1));
        if (Number.isNaN(pct)) return 1;
        return Math.max(0, Math.min(1, pct / 100));
      }
      const n = Number.parseFloat(value);
      if (Number.isNaN(n)) return 1;
      return Math.max(0, Math.min(1, n));
    };

    const r = parseRgbComponent(parts[0]);
    const g = parseRgbComponent(parts[1]);
    const b = parseRgbComponent(parts[2]);
    const a = parseAlpha(parts[3]);

    return { r, g, b, a };
  };

  const toRgba = (input: string): RGBA | null => {
    if (input.startsWith("#")) return parseHex(input);
    if (input.toLowerCase().startsWith("rgb")) return parseRgbFunction(input);
    return null;
  };

  const compositeOverWhite = (
    rgba: RGBA,
  ): { r: number; g: number; b: number } => {
    const background = { r: 255, g: 255, b: 255 };
    const a = rgba.a;
    const r = clamp0To255(rgba.r * a + background.r * (1 - a));
    const g = clamp0To255(rgba.g * a + background.g * (1 - a));
    const b = clamp0To255(rgba.b * a + background.b * (1 - a));
    return { r, g, b };
  };

  const rgba = toRgba(color);
  if (!rgba) return false;

  const composited = compositeOverWhite(rgba);
  const brightness =
    (composited.r * 0.299 + composited.g * 0.587 + composited.b * 0.114) / 255;
  return brightness < 0.5;
};

/**
 * Restylane brand product colors, used as preset swatches in the color picker
 * and the scribe annotator. Single source of truth for both.
 */
export const RESTYLANE_COLORS = [
  "#9787CC", // Restylane Lyft (purple)
  "#A4D8DE", // Restylane Defyne (cyan)
  "#F2BAB7", // Restylane Kysse (pink)
  "#BFB5DA", // Restylane Refyne (lavender)
  "#A8C8DE", // Restylane Silk (light blue)
  "#D85C5A", // Restylane Contour (coral red)
] as const;

/**
 * A palette of ~12 legible, accessible hues used to auto-assign a stable color
 * to a provider when they have no explicit calendar color set.
 */
export const PROVIDER_AUTO_PALETTE: string[] = [
  "#3B82F6", // blue
  "#10B981", // emerald
  "#F59E0B", // amber
  "#EF4444", // red
  "#8B5CF6", // violet
  "#EC4899", // pink
  "#14B8A6", // teal
  "#F97316", // orange
  "#6366F1", // indigo
  "#84CC16", // lime
  "#06B6D4", // cyan
  "#A855F7", // purple
];

/**
 * Deterministically maps a provider id to one of the legible palette hues.
 * The same id always yields the same color across renders/sessions.
 */
export const getProviderAutoColor = (providerId: string): string => {
  let hash = 0;
  for (let i = 0; i < providerId.length; i++) {
    hash = (hash * 31 + providerId.charCodeAt(i)) | 0;
  }
  const index = Math.abs(hash) % PROVIDER_AUTO_PALETTE.length;
  return PROVIDER_AUTO_PALETTE[index];
};

export const hexToRgba = (hex: string, alpha: number): string => {
  if (!hex) return "";

  // Remove the hash if it exists
  hex = hex.replace("#", "");

  // Parse the hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Return as rgba
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const darkenColor = (
  hex: string | undefined,
  percent: number = 30,
): string => {
  if (!hex) return "";

  // Remove the hash if it exists
  hex = hex.replace("#", "");

  // Parse the hex values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Darken each component
  r = Math.max(0, Math.floor((r * (100 - percent)) / 100));
  g = Math.max(0, Math.floor((g * (100 - percent)) / 100));
  b = Math.max(0, Math.floor((b * (100 - percent)) / 100));

  // Convert back to hex
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

export const lightenColor = (
  hex: string | undefined,
  percent: number = 30,
): string => {
  if (!hex) return "";

  // Remove the hash if it exists
  hex = hex.replace("#", "");

  // Parse the hex values
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);

  // Lighten each component
  r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
  g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
  b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

  // Convert back to hex
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};
